/**
 * Product Personalization Schemas — Data-driven system for per-product personalization.
 * Each product defines 3–5 steps with questions that meaningfully affect generation.
 *
 * Field types: text, textarea, date, number, select, visual-select, color-palette,
 *              multi-select, chips, toggle
 */

// ─── Shared style options ───
const weddingStyles = [
  { id: 'minimal-timeless', label: 'Minimal & Timeless', desc: 'Clean lines, simple elegance' },
  { id: 'modern-luxury', label: 'Modern Luxury', desc: 'Sleek, contemporary, high-end' },
  { id: 'romantic', label: 'Romantic', desc: 'Soft, dreamy, delicate details' },
  { id: 'botanical-garden', label: 'Botanical / Garden', desc: 'Greenery, florals, organic' },
  { id: 'editorial-black-tie', label: 'Editorial / Black Tie', desc: 'Dramatic, formal, sophisticated' },
  { id: 'coastal', label: 'Coastal', desc: 'Ocean blues, sand, relaxed elegance' },
  { id: 'rustic-boho', label: 'Rustic / Boho', desc: 'Warm earth tones, wildflowers' },
  { id: 'soft-feminine', label: 'Soft Feminine', desc: 'Blush, lace, gentle touches' },
];

const weddingPalettes = [
  { id: 'ivory-champagne', label: 'Ivory + Champagne', colors: ['#FFFFF0', '#F7E7CE', '#D4C5A9'] },
  { id: 'blush-cream', label: 'Blush + Cream', colors: ['#F4C2C2', '#FFF5EE', '#E8D5C4'] },
  { id: 'sage-ivory', label: 'Sage + Ivory', colors: ['#9CAF88', '#C1CFA1', '#FFFFF0'] },
  { id: 'dusty-blue-cream', label: 'Dusty Blue + Cream', colors: ['#6B8E9B', '#A8C4D4', '#FFF5EE'] },
  { id: 'black-white', label: 'Black + White', colors: ['#1A1A1A', '#FFFFFF', '#E8E8E8'] },
  { id: 'terracotta-beige', label: 'Terracotta + Beige', colors: ['#C06014', '#D4956A', '#F5F0E8'] },
  { id: 'soft-pink-champagne', label: 'Soft Pink + Champagne', colors: ['#FFB6C1', '#F7E7CE', '#FFF5EE'] },
  { id: 'custom', label: 'Custom colors', colors: [] },
];

const babyStyles = [
  { id: 'soft-neutral', label: 'Soft Neutral', desc: 'Warm beige and cream tones' },
  { id: 'pastel', label: 'Pastel', desc: 'Light, airy pastel colors' },
  { id: 'botanical', label: 'Botanical', desc: 'Greenery and nature elements' },
  { id: 'minimal', label: 'Minimal', desc: 'Clean and simple' },
  { id: 'playful', label: 'Playful', desc: 'Fun patterns and bright accents' },
  { id: 'classic', label: 'Classic', desc: 'Timeless, traditional elegance' },
];

const fitnessStyles = [
  { id: 'bold-dark', label: 'Bold & Dark', desc: 'High-contrast dark mode' },
  { id: 'minimal-clean', label: 'Minimal & Clean', desc: 'White space, simple lines' },
  { id: 'energetic', label: 'Energetic', desc: 'Bright accents, dynamic feel' },
  { id: 'earth-tones', label: 'Earth Tones', desc: 'Warm, grounding colors' },
  { id: 'modern-sport', label: 'Modern Sport', desc: 'Athletic, professional look' },
  { id: 'soft-wellness', label: 'Soft Wellness', desc: 'Calm, balanced aesthetic' },
];

const financeStyles = [
  { id: 'minimal', label: 'Minimal', desc: 'Clean and uncluttered' },
  { id: 'soft-feminine', label: 'Soft Feminine', desc: 'Warm blush and cream' },
  { id: 'professional', label: 'Professional', desc: 'Corporate, polished look' },
  { id: 'colorful', label: 'Colorful', desc: 'Vibrant and motivating' },
  { id: 'neutral', label: 'Neutral', desc: 'Calm earth tones' },
];

const journalStyles = [
  { id: 'soft-neutral', label: 'Soft Neutral', desc: 'Warm beige, calming' },
  { id: 'minimal', label: 'Minimal', desc: 'Clean white space' },
  { id: 'botanical', label: 'Botanical', desc: 'Nature-inspired greens' },
  { id: 'feminine', label: 'Feminine', desc: 'Blush, soft pink tones' },
  { id: 'modern', label: 'Modern', desc: 'Bold, contemporary feel' },
  { id: 'warm-earthy', label: 'Warm Earthy', desc: 'Rich browns and terracotta' },
];

// ─── SCHEMAS ───

const schemas = {
  // ═══════════════════════════════════════
  // 1. WEDDING PLANNER
  // ═══════════════════════════════════════
  'wedding-planner': {
    productType: 'journal',
    showCreatorToggle: true,
    creatorFields: [
      { id: 'targetBuyer', type: 'text', label: 'Target buyer', placeholder: 'e.g. Brides-to-be planning a luxury wedding' },
      { id: 'brandStyle', type: 'text', label: 'Your brand style', placeholder: 'e.g. Minimalist, elegant, Canva-ready' },
    ],
    steps: [
      {
        id: 'details',
        title: 'Your Wedding',
        subtitle: 'Tell us about your special day',
        fields: [
          { id: 'partner1', type: 'text', label: 'Partner 1 name', required: true, placeholder: 'First name' },
          { id: 'partner2', type: 'text', label: 'Partner 2 name', required: true, placeholder: 'First name' },
          { id: 'weddingDate', type: 'date', label: 'Wedding date', required: false },
          { id: 'venue', type: 'text', label: 'Wedding location / venue', required: false, placeholder: 'e.g. Garden, Beach, Vineyard' },
        ]
      },
      {
        id: 'style',
        title: 'Choose Your Wedding Style',
        subtitle: 'Select the aesthetic that matches your vision',
        type: 'visual-select',
        field: 'weddingStyle',
        required: true,
        options: weddingStyles,
      },
      {
        id: 'palette',
        title: 'Choose Your Color Palette',
        subtitle: 'Pick the colors for your wedding planner',
        type: 'color-palette',
        field: 'colorPalette',
        required: true,
        options: weddingPalettes,
      },
      {
        id: 'personal',
        title: 'Personal Touches',
        subtitle: 'Customize the final details',
        fields: [
          { id: 'coverFormat', type: 'select', label: 'Cover format', required: true, options: [
            { id: 'full-names', label: 'Full names' },
            { id: 'first-names', label: 'First names only' },
            { id: 'initials', label: 'Initials' },
          ]},
          { id: 'typography', type: 'select', label: 'Typography preference', required: true, options: [
            { id: 'elegant-serif', label: 'Elegant serif' },
            { id: 'modern', label: 'Modern sans-serif' },
            { id: 'romantic-script', label: 'Romantic script' },
            { id: 'editorial', label: 'Editorial' },
          ]},
          { id: 'specialNote', type: 'textarea', label: 'Anything special you\'d like included?', required: false, placeholder: 'e.g. Specific sections, themes, or details...' },
        ]
      },
    ]
  },

  // ═══════════════════════════════════════
  // 2. WEDDING GUEST BOOK
  // ═══════════════════════════════════════
  'wedding-guest-book': {
    productType: 'memory-book',
    showCreatorToggle: true,
    creatorFields: [
      { id: 'targetBuyer', type: 'text', label: 'Target buyer', placeholder: 'e.g. Engaged couples planning a wedding' },
    ],
    steps: [
      {
        id: 'details',
        title: 'Event Details',
        subtitle: 'Tell us about the couple',
        fields: [
          { id: 'coupleNames', type: 'text', label: 'Couple\'s names', required: true, placeholder: 'e.g. Emma & James' },
          { id: 'weddingDate', type: 'date', label: 'Wedding date', required: false },
          { id: 'venue', type: 'text', label: 'Venue / location', required: false, placeholder: 'e.g. The Grand Hotel' },
        ]
      },
      {
        id: 'style',
        title: 'Guest Book Style',
        subtitle: 'Choose the look and feel',
        type: 'visual-select',
        field: 'bookStyle',
        required: true,
        options: [
          { id: 'romantic', label: 'Romantic', desc: 'Soft, dreamy, elegant' },
          { id: 'minimal', label: 'Minimal', desc: 'Clean and modern' },
          { id: 'luxury', label: 'Luxury', desc: 'Gold accents, premium feel' },
          { id: 'botanical', label: 'Botanical', desc: 'Greenery and florals' },
          { id: 'modern', label: 'Modern', desc: 'Contemporary, bold' },
          { id: 'editorial', label: 'Editorial', desc: 'Magazine-style elegance' },
        ],
      },
      {
        id: 'palette',
        title: 'Color Palette',
        subtitle: 'Select colors for your guest book',
        type: 'color-palette',
        field: 'colorPalette',
        required: true,
        options: weddingPalettes,
      },
      {
        id: 'format',
        title: 'Guest Book Format',
        subtitle: 'What should guests share?',
        type: 'multi-select',
        field: 'guestBookSections',
        required: true,
        options: [
          { id: 'messages-wishes', label: 'Messages & wishes' },
          { id: 'polaroid-photo', label: 'Polaroid / photo guest book' },
          { id: 'advice-newlyweds', label: 'Advice for the newlyweds' },
          { id: 'memories-couple', label: 'Memories with the couple' },
          { id: 'combination', label: 'Combination of all' },
        ],
      },
    ]
  },

  // ═══════════════════════════════════════
  // 3. PREGNANCY JOURNAL
  // ═══════════════════════════════════════
  'pregnancy-journal': {
    productType: 'pregnancy-journal',
    showCreatorToggle: true,
    creatorFields: [
      { id: 'targetBuyer', type: 'text', label: 'Target buyer', placeholder: 'e.g. First-time moms, expecting parents' },
    ],
    steps: [
      {
        id: 'details',
        title: 'About the Journey',
        subtitle: 'Tell us about your pregnancy',
        fields: [
          { id: 'parentName', type: 'text', label: 'Mother\'s / parent\'s name', required: false, placeholder: 'Optional' },
          { id: 'babyName', type: 'text', label: 'Baby nickname / name', required: false, placeholder: 'Optional' },
          { id: 'dueDate', type: 'date', label: 'Due date', required: false },
          { id: 'firstPregnancy', type: 'select', label: 'First pregnancy?', required: false, options: [
            { id: 'yes', label: 'Yes' }, { id: 'no', label: 'No' },
          ]},
        ]
      },
      {
        id: 'style',
        title: 'Journal Style',
        subtitle: 'Choose the aesthetic for your journal',
        type: 'visual-select',
        field: 'journalStyle',
        required: true,
        options: journalStyles,
      },
      {
        id: 'focus',
        title: 'What Should the Journal Focus On?',
        subtitle: 'Select all that matter to you',
        type: 'multi-select',
        field: 'journalFocus',
        required: true,
        options: [
          { id: 'weekly-memories', label: 'Weekly pregnancy memories' },
          { id: 'appointments', label: 'Appointments' },
          { id: 'symptoms', label: 'Symptoms' },
          { id: 'bump-photos', label: 'Bump photos' },
          { id: 'baby-prep', label: 'Baby preparations' },
          { id: 'name-ideas', label: 'Name ideas' },
          { id: 'birth-prep', label: 'Birth preparation' },
          { id: 'letters-to-baby', label: 'Letters to baby' },
        ],
      },
    ]
  },

  // ═══════════════════════════════════════
  // 4. BABY MILESTONE BOOK
  // ═══════════════════════════════════════
  'baby-milestone-book': {
    productType: 'memory-book',
    showCreatorToggle: true,
    creatorFields: [
      { id: 'targetBuyer', type: 'text', label: 'Target buyer', placeholder: 'e.g. New parents, baby shower gifts' },
    ],
    steps: [
      {
        id: 'details',
        title: 'About Your Baby',
        subtitle: 'Personalize the milestone book',
        fields: [
          { id: 'babyName', type: 'text', label: 'Baby\'s name', required: false, placeholder: 'Optional' },
          { id: 'birthDate', type: 'date', label: 'Birth date', required: false },
          { id: 'wording', type: 'select', label: 'Preferred wording', required: true, options: [
            { id: 'boy', label: 'Boy' }, { id: 'girl', label: 'Girl' }, { id: 'neutral', label: 'Gender-neutral' },
          ]},
        ]
      },
      {
        id: 'style',
        title: 'Design Style',
        subtitle: 'Choose the look for the milestone book',
        type: 'visual-select',
        field: 'bookStyle',
        required: true,
        options: babyStyles,
      },
      {
        id: 'memories',
        title: 'Which Memories to Emphasize?',
        subtitle: 'Select what matters most',
        type: 'multi-select',
        field: 'milestones',
        required: true,
        options: [
          { id: 'first-year', label: 'First year' },
          { id: 'monthly', label: 'Monthly milestones' },
          { id: 'first-words', label: 'First words' },
          { id: 'first-foods', label: 'First foods' },
          { id: 'first-steps', label: 'First steps' },
          { id: 'family', label: 'Family memories' },
          { id: 'holidays', label: 'Holidays' },
          { id: 'photos', label: 'Photos' },
        ],
      },
    ]
  },

  // ═══════════════════════════════════════
  // 5. BABY SLEEP TRACKER
  // ═══════════════════════════════════════
  'baby-sleep-tracker': {
    productType: 'habit-tracker',
    showCreatorToggle: false,
    steps: [
      {
        id: 'details',
        title: 'About Your Baby',
        subtitle: 'Quick setup for sleep tracking',
        fields: [
          { id: 'babyName', type: 'text', label: 'Baby\'s name', required: false, placeholder: 'Optional' },
          { id: 'babyAge', type: 'text', label: 'Baby\'s age', required: true, placeholder: 'e.g. 4 months' },
          { id: 'primaryGoal', type: 'select', label: 'Primary goal', required: true, options: [
            { id: 'understand-patterns', label: 'Understand sleep patterns' },
            { id: 'improve-routine', label: 'Improve routine' },
            { id: 'track-naps', label: 'Track naps' },
            { id: 'track-night-wakings', label: 'Track night wakings' },
            { id: 'complete-overview', label: 'Complete sleep overview' },
          ]},
        ]
      },
      {
        id: 'extras',
        title: 'Additional Tracking',
        subtitle: 'What else would you like to track?',
        type: 'multi-select',
        field: 'additionalTracking',
        required: false,
        options: [
          { id: 'feeding', label: 'Feeding tracking' },
          { id: 'diapers', label: 'Diaper tracking' },
          { id: 'mood', label: 'Mood' },
          { id: 'notes', label: 'Notes' },
        ],
      },
      {
        id: 'style',
        title: 'Style Preference',
        subtitle: 'Choose a look',
        type: 'visual-select',
        field: 'trackerStyle',
        required: true,
        options: babyStyles.slice(0, 4),
      },
    ]
  },

  // ═══════════════════════════════════════
  // 6. AI TRAVEL PLANNER
  // ═══════════════════════════════════════
  'ai-travel-planner': {
    productType: 'travel-planner',
    showCreatorToggle: false,
    steps: [
      {
        id: 'trip',
        title: 'Your Trip',
        subtitle: 'Where are you headed?',
        fields: [
          { id: 'destination', type: 'text', label: 'Destination', required: true, placeholder: 'e.g. Tokyo, Japan' },
          { id: 'departureDate', type: 'date', label: 'Departure date', required: false },
          { id: 'returnDate', type: 'date', label: 'Return date', required: false },
          { id: 'travelers', type: 'number', label: 'Number of travelers', required: false, placeholder: '2', min: 1, max: 20 },
          { id: 'travelWith', type: 'select', label: 'Who are you traveling with?', required: true, options: [
            { id: 'solo', label: 'Solo' }, { id: 'partner', label: 'Partner' },
            { id: 'friends', label: 'Friends' }, { id: 'family', label: 'Family' },
            { id: 'children', label: 'With children' },
          ]},
        ]
      },
      {
        id: 'travelStyle',
        title: 'Your Travel Style',
        subtitle: 'Select all that apply',
        type: 'multi-select',
        field: 'travelInterests',
        required: true,
        options: [
          { id: 'relaxing', label: 'Relaxing' }, { id: 'luxury', label: 'Luxury' },
          { id: 'budget', label: 'Budget-friendly' }, { id: 'food', label: 'Food & restaurants' },
          { id: 'culture', label: 'Culture' }, { id: 'shopping', label: 'Shopping' },
          { id: 'adventure', label: 'Adventure' }, { id: 'nightlife', label: 'Nightlife' },
          { id: 'beaches', label: 'Beaches' }, { id: 'nature', label: 'Nature' },
        ],
      },
      {
        id: 'pace',
        title: 'Trip Pace & Budget',
        subtitle: 'How do you want to travel?',
        fields: [
          { id: 'tripPace', type: 'select', label: 'Trip pace', required: true, options: [
            { id: 'relaxed', label: 'Relaxed' }, { id: 'balanced', label: 'Balanced' },
            { id: 'packed', label: 'See as much as possible' },
          ]},
          { id: 'tripBudget', type: 'select', label: 'Budget level', required: false, options: [
            { id: 'budget', label: 'Budget' }, { id: 'mid-range', label: 'Mid-range' },
            { id: 'premium', label: 'Premium' }, { id: 'luxury', label: 'Luxury' },
          ]},
        ]
      },
    ]
  },

  // ═══════════════════════════════════════
  // 7. TRAVEL JOURNAL
  // ═══════════════════════════════════════
  'travel-journal': {
    productType: 'journal',
    showCreatorToggle: true,
    creatorFields: [
      { id: 'targetBuyer', type: 'text', label: 'Target buyer', placeholder: 'e.g. Travel enthusiasts, backpackers' },
    ],
    steps: [
      {
        id: 'details',
        title: 'Your Trip',
        subtitle: 'Where are you journaling about?',
        fields: [
          { id: 'destination', type: 'text', label: 'Destination', required: true, placeholder: 'e.g. Italy, Southeast Asia' },
          { id: 'travelDates', type: 'text', label: 'Travel dates', required: false, placeholder: 'e.g. June 15–28' },
          { id: 'travelerNames', type: 'text', label: 'Traveler name(s)', required: false, placeholder: 'Optional' },
        ]
      },
      {
        id: 'focus',
        title: 'Journal Focus',
        subtitle: 'What do you want to capture?',
        type: 'multi-select',
        field: 'journalFocus',
        required: true,
        options: [
          { id: 'daily-memories', label: 'Daily memories' }, { id: 'photos', label: 'Photos' },
          { id: 'food', label: 'Food discoveries' }, { id: 'favorite-places', label: 'Favorite places' },
          { id: 'people', label: 'People we met' }, { id: 'highlights', label: 'Highlights' },
          { id: 'expenses', label: 'Expenses' }, { id: 'bucket-list', label: 'Bucket list' },
        ],
      },
      {
        id: 'style',
        title: 'Journal Style',
        subtitle: 'Pick an aesthetic',
        type: 'visual-select',
        field: 'journalStyle',
        required: true,
        options: [
          { id: 'minimal', label: 'Minimal', desc: 'Clean and simple' },
          { id: 'adventure', label: 'Adventure', desc: 'Bold, outdoor vibe' },
          { id: 'vintage', label: 'Vintage Travel', desc: 'Retro, nostalgic feel' },
          { id: 'coastal', label: 'Coastal', desc: 'Beach and ocean inspired' },
          { id: 'luxury', label: 'Luxury', desc: 'Premium, editorial feel' },
          { id: 'scrapbook', label: 'Scrapbook', desc: 'Creative, collage style' },
        ],
      },
    ]
  },

  // ═══════════════════════════════════════
  // 8. BUDGET TRACKER
  // ═══════════════════════════════════════
  'budget-tracker': {
    productType: 'budget-planner',
    showCreatorToggle: true,
    creatorFields: [
      { id: 'targetBuyer', type: 'text', label: 'Target buyer', placeholder: 'e.g. Young professionals, families' },
    ],
    steps: [
      {
        id: 'details',
        title: 'Your Finances',
        subtitle: 'Set up your budget tracker',
        fields: [
          { id: 'timeframe', type: 'select', label: 'Monthly or yearly planner?', required: true, options: [
            { id: 'monthly', label: 'Monthly' }, { id: 'yearly', label: 'Yearly' },
          ]},
          { id: 'currency', type: 'select', label: 'Currency', required: true, options: [
            { id: 'usd', label: 'USD ($)' }, { id: 'eur', label: 'EUR (€)' },
            { id: 'gbp', label: 'GBP (£)' }, { id: 'nok', label: 'NOK (kr)' },
            { id: 'sek', label: 'SEK (kr)' }, { id: 'other', label: 'Other' },
          ]},
          { id: 'mainGoal', type: 'select', label: 'Main goal', required: true, options: [
            { id: 'control-spending', label: 'Control spending' }, { id: 'save-more', label: 'Save more' },
            { id: 'emergency-fund', label: 'Build emergency fund' }, { id: 'pay-debt', label: 'Pay down debt' },
            { id: 'household', label: 'Household budgeting' }, { id: 'overview', label: 'Business / personal overview' },
          ]},
        ]
      },
      {
        id: 'tracking',
        title: 'What Do You Want to Track?',
        subtitle: 'Select all that apply',
        type: 'multi-select',
        field: 'trackingSections',
        required: true,
        options: [
          { id: 'income', label: 'Income' }, { id: 'fixed-expenses', label: 'Fixed expenses' },
          { id: 'variable-expenses', label: 'Variable expenses' }, { id: 'subscriptions', label: 'Subscriptions' },
          { id: 'savings', label: 'Savings' }, { id: 'debt', label: 'Debt' },
          { id: 'bills', label: 'Bills' }, { id: 'sinking-funds', label: 'Sinking funds' },
        ],
      },
      {
        id: 'style',
        title: 'Design Style',
        subtitle: 'Choose the look',
        type: 'visual-select',
        field: 'designStyle',
        required: true,
        options: financeStyles,
      },
    ]
  },

  // ═══════════════════════════════════════
  // 9. SAVINGS GOAL TRACKER
  // ═══════════════════════════════════════
  'savings-goal-tracker': {
    productType: 'savings-tracker',
    showCreatorToggle: false,
    steps: [
      {
        id: 'goal',
        title: 'Your Savings Goal',
        subtitle: 'What are you saving for?',
        fields: [
          { id: 'savingFor', type: 'text', label: 'What are you saving for?', required: true, placeholder: 'e.g. Vacation, Emergency fund, New car' },
          { id: 'targetAmount', type: 'text', label: 'Target amount', required: true, placeholder: 'e.g. $5,000' },
          { id: 'currentSavings', type: 'text', label: 'Current savings', required: false, placeholder: 'Optional' },
          { id: 'targetDate', type: 'date', label: 'Target date', required: false },
          { id: 'currency', type: 'select', label: 'Currency', required: true, options: [
            { id: 'usd', label: 'USD ($)' }, { id: 'eur', label: 'EUR (€)' },
            { id: 'gbp', label: 'GBP (£)' }, { id: 'nok', label: 'NOK (kr)' },
          ]},
        ]
      },
      {
        id: 'frequency',
        title: 'Saving Frequency',
        subtitle: 'How do you want to save?',
        type: 'visual-select',
        field: 'savingFrequency',
        required: true,
        options: [
          { id: 'weekly', label: 'Weekly saving', desc: 'Small consistent amounts' },
          { id: 'monthly', label: 'Monthly saving', desc: 'Larger monthly deposits' },
          { id: 'flexible', label: 'Flexible saving', desc: 'Save when you can' },
        ],
      },
      {
        id: 'style',
        title: 'Design Style',
        subtitle: 'Choose a look',
        type: 'visual-select',
        field: 'designStyle',
        required: true,
        options: financeStyles,
      },
    ]
  },

  // ═══════════════════════════════════════
  // 10. DEBT PAYOFF TRACKER
  // ═══════════════════════════════════════
  'debt-payoff-tracker': {
    productType: 'savings-tracker',
    showCreatorToggle: false,
    steps: [
      {
        id: 'debts',
        title: 'Your Debt Overview',
        subtitle: 'Set up your payoff tracker',
        fields: [
          { id: 'numDebts', type: 'number', label: 'Number of debts', required: true, placeholder: 'e.g. 3', min: 1, max: 20 },
          { id: 'totalDebt', type: 'text', label: 'Approximate total debt', required: false, placeholder: 'Optional' },
          { id: 'currency', type: 'select', label: 'Currency', required: true, options: [
            { id: 'usd', label: 'USD ($)' }, { id: 'eur', label: 'EUR (€)' },
            { id: 'gbp', label: 'GBP (£)' }, { id: 'nok', label: 'NOK (kr)' },
          ]},
          { id: 'strategy', type: 'select', label: 'Payoff strategy', required: true, options: [
            { id: 'snowball', label: 'Snowball (smallest first)' },
            { id: 'avalanche', label: 'Avalanche (highest interest first)' },
            { id: 'unsure', label: 'Not sure — let Lumera suggest' },
          ]},
        ]
      },
      {
        id: 'features',
        title: 'What to Include?',
        subtitle: 'Select features for your tracker',
        type: 'multi-select',
        field: 'trackerFeatures',
        required: true,
        options: [
          { id: 'monthly-payments', label: 'Monthly payment tracker' },
          { id: 'progress-bars', label: 'Progress bars' },
          { id: 'milestone-rewards', label: 'Milestone rewards' },
          { id: 'interest-tracking', label: 'Interest tracking' },
          { id: 'payoff-projections', label: 'Payoff projections' },
        ],
      },
      {
        id: 'style',
        title: 'Design Style',
        subtitle: 'Choose a look',
        type: 'visual-select',
        field: 'designStyle',
        required: true,
        options: financeStyles,
      },
    ]
  },

  // ═══════════════════════════════════════
  // 11. FITNESS JOURNAL
  // ═══════════════════════════════════════
  'fitness-journal': {
    productType: 'journal',
    showCreatorToggle: true,
    creatorFields: [
      { id: 'targetBuyer', type: 'text', label: 'Target buyer', placeholder: 'e.g. Gym beginners, women who lift' },
    ],
    steps: [
      {
        id: 'goal',
        title: 'Your Fitness Goal',
        subtitle: 'What are you working toward?',
        type: 'visual-select',
        field: 'fitnessGoal',
        required: true,
        options: [
          { id: 'build-muscle', label: 'Build muscle', desc: 'Strength and size' },
          { id: 'lose-fat', label: 'Lose fat', desc: 'Lean and toned' },
          { id: 'improve-fitness', label: 'Improve fitness', desc: 'Overall health' },
          { id: 'get-stronger', label: 'Get stronger', desc: 'Increase strength' },
          { id: 'maintain', label: 'Maintain', desc: 'Stay on track' },
          { id: 'general-wellness', label: 'General wellness', desc: 'Mind and body' },
        ],
      },
      {
        id: 'training',
        title: 'Training Details',
        subtitle: 'How do you train?',
        fields: [
          { id: 'trainingLocation', type: 'select', label: 'Training location', required: true, options: [
            { id: 'gym', label: 'Gym' }, { id: 'home', label: 'Home' },
            { id: 'running', label: 'Running / outdoor' }, { id: 'mixed', label: 'Mixed' },
          ]},
          { id: 'frequency', type: 'select', label: 'Days per week', required: true, options: [
            { id: '2', label: '2 days' }, { id: '3', label: '3 days' }, { id: '4', label: '4 days' },
            { id: '5', label: '5 days' }, { id: '6', label: '6 days' },
          ]},
        ]
      },
      {
        id: 'tracking',
        title: 'What to Track?',
        subtitle: 'Select all that apply',
        type: 'multi-select',
        field: 'trackingItems',
        required: true,
        options: [
          { id: 'workouts', label: 'Workouts' }, { id: 'weight-reps', label: 'Weight / reps' },
          { id: 'measurements', label: 'Measurements' }, { id: 'progress-photos', label: 'Progress photos' },
          { id: 'steps', label: 'Steps' }, { id: 'water', label: 'Water' },
          { id: 'sleep', label: 'Sleep' }, { id: 'mood', label: 'Mood' },
        ],
      },
      {
        id: 'style',
        title: 'Design Style',
        subtitle: 'Choose the look',
        type: 'visual-select',
        field: 'designStyle',
        required: true,
        options: fitnessStyles,
      },
    ]
  },

  // ═══════════════════════════════════════
  // 12. 12-WEEK WORKOUT
  // ═══════════════════════════════════════
  '12-week-workout': {
    productType: 'fitness-program',
    showCreatorToggle: true,
    creatorFields: [
      { id: 'targetBuyer', type: 'text', label: 'Target buyer', placeholder: 'e.g. Women who want to start lifting' },
    ],
    steps: [
      {
        id: 'goal',
        title: 'Your Fitness Goal',
        subtitle: 'What do you want to achieve in 12 weeks?',
        fields: [
          { id: 'primaryGoal', type: 'select', label: 'Primary goal', required: true, options: [
            { id: 'build-muscle', label: 'Build muscle' }, { id: 'lose-fat', label: 'Lose fat' },
            { id: 'improve-fitness', label: 'Improve overall fitness' }, { id: 'get-stronger', label: 'Get stronger' },
            { id: 'tone', label: 'Tone and define' },
          ]},
          { id: 'level', type: 'select', label: 'Current level', required: true, options: [
            { id: 'beginner', label: 'Beginner' }, { id: 'intermediate', label: 'Intermediate' },
            { id: 'advanced', label: 'Advanced' },
          ]},
          { id: 'location', type: 'select', label: 'Training location', required: true, options: [
            { id: 'home', label: 'Home' }, { id: 'gym', label: 'Gym' }, { id: 'both', label: 'Both' },
          ]},
        ]
      },
      {
        id: 'schedule',
        title: 'Your Schedule',
        subtitle: 'How much time can you commit?',
        fields: [
          { id: 'daysPerWeek', type: 'select', label: 'Days available per week', required: true, options: [
            { id: '3', label: '3 days' }, { id: '4', label: '4 days' },
            { id: '5', label: '5 days' }, { id: '6', label: '6 days' },
          ]},
          { id: 'duration', type: 'select', label: 'Workout duration', required: true, options: [
            { id: '30', label: '~30 minutes' }, { id: '45', label: '~45 minutes' },
            { id: '60', label: '~60 minutes' }, { id: '75', label: '75+ minutes' },
          ]},
          { id: 'equipment', type: 'select', label: 'Equipment', required: true, options: [
            { id: 'none', label: 'No equipment' }, { id: 'dumbbells', label: 'Dumbbells' },
            { id: 'bands', label: 'Resistance bands' }, { id: 'full-gym', label: 'Full gym' },
          ]},
        ]
      },
      {
        id: 'extras',
        title: 'Anything Else?',
        subtitle: 'Optional details',
        fields: [
          { id: 'specialNote', type: 'textarea', label: 'Anything we should consider?', required: false, placeholder: 'e.g. Bad knees, prefer upper body focus, etc.' },
        ]
      },
    ]
  },

  // ═══════════════════════════════════════
  // 13. RUNNING PROGRAM
  // ═══════════════════════════════════════
  'running-program': {
    productType: 'fitness-program',
    showCreatorToggle: false,
    steps: [
      {
        id: 'level',
        title: 'Your Running Level',
        subtitle: 'Where are you starting from?',
        fields: [
          { id: 'currentLevel', type: 'select', label: 'Current level', required: true, options: [
            { id: 'never', label: 'Never run before' }, { id: 'beginner', label: 'Beginner (occasional runs)' },
            { id: 'intermediate', label: 'Intermediate (regular runner)' }, { id: 'advanced', label: 'Advanced' },
          ]},
          { id: 'currentFrequency', type: 'select', label: 'Current weekly running', required: true, options: [
            { id: '0', label: 'Not running yet' }, { id: '1-2', label: '1–2 times/week' },
            { id: '3-4', label: '3–4 times/week' }, { id: '5+', label: '5+ times/week' },
          ]},
        ]
      },
      {
        id: 'goal',
        title: 'Your Goal',
        subtitle: 'What are you training for?',
        fields: [
          { id: 'runningGoal', type: 'select', label: 'Goal', required: true, options: [
            { id: 'start', label: 'Start running' }, { id: '5k', label: 'Run 5K' },
            { id: '10k', label: 'Run 10K' }, { id: 'half-marathon', label: 'Half marathon' },
            { id: 'pace', label: 'Improve pace' }, { id: 'endurance', label: 'General endurance' },
          ]},
          { id: 'goalDate', type: 'date', label: 'Goal date', required: false },
          { id: 'daysPerWeek', type: 'select', label: 'Days available per week', required: true, options: [
            { id: '3', label: '3 days' }, { id: '4', label: '4 days' }, { id: '5', label: '5 days' },
          ]},
        ]
      },
      {
        id: 'extras',
        title: 'Additional Features',
        subtitle: 'Include in the program?',
        type: 'multi-select',
        field: 'programExtras',
        required: false,
        options: [
          { id: 'strength', label: 'Strength sessions' }, { id: 'mobility', label: 'Mobility' },
          { id: 'recovery', label: 'Recovery' }, { id: 'progress-tracking', label: 'Progress tracking' },
        ],
      },
    ]
  },

  // ═══════════════════════════════════════
  // 14. HIGH-PROTEIN MEAL PLAN
  // ═══════════════════════════════════════
  'high-protein-meal-plan': {
    productType: 'meal-planner',
    showCreatorToggle: false,
    steps: [
      {
        id: 'goal',
        title: 'Your Nutrition Goal',
        subtitle: 'What are you aiming for?',
        fields: [
          { id: 'nutritionGoal', type: 'select', label: 'Goal', required: true, options: [
            { id: 'healthy', label: 'General healthy eating' }, { id: 'muscle', label: 'Muscle gain' },
            { id: 'weight', label: 'Weight management' }, { id: 'fitness', label: 'Fitness support' },
          ]},
          { id: 'mealsPerDay', type: 'select', label: 'Meals per day', required: true, options: [
            { id: '3', label: '3 meals' }, { id: '4', label: '4 meals' }, { id: '5', label: '5 meals' },
          ]},
          { id: 'daysToPlain', type: 'select', label: 'Days to plan', required: true, options: [
            { id: '5', label: '5 days' }, { id: '7', label: '7 days' }, { id: '14', label: '14 days' },
          ]},
        ]
      },
      {
        id: 'dietary',
        title: 'Dietary Preferences',
        subtitle: 'Any restrictions?',
        fields: [
          { id: 'dietaryPref', type: 'select', label: 'Dietary preference', required: true, options: [
            { id: 'none', label: 'No preference' }, { id: 'vegetarian', label: 'Vegetarianian' },
            { id: 'pescatarian', label: 'Pescatarian' }, { id: 'dairy-free', label: 'Dairy-free' },
            { id: 'gluten-free', label: 'Gluten-free' }, { id: 'custom', label: 'Custom' },
          ]},
          { id: 'allergies', type: 'text', label: 'Foods to avoid / allergies', required: false, placeholder: 'Optional' },
          { id: 'cookingPref', type: 'select', label: 'Cooking preference', required: true, options: [
            { id: 'quick', label: 'Quick (under 20 min)' }, { id: 'normal', label: 'Normal' },
            { id: 'mealprep', label: 'Meal prep' },
          ]},
        ]
      },
    ]
  },

  // ═══════════════════════════════════════
  // 15. SELF CARE JOURNAL
  // ═══════════════════════════════════════
  'self-care-journal': {
    productType: 'journal',
    showCreatorToggle: true,
    creatorFields: [
      { id: 'targetBuyer', type: 'text', label: 'Target buyer', placeholder: 'e.g. Women seeking daily wellness rituals' },
    ],
    steps: [
      {
        id: 'focus',
        title: 'What Would You Like to Focus On?',
        subtitle: 'Select all that resonate with you',
        type: 'multi-select',
        field: 'selfCareFocus',
        required: true,
        options: [
          { id: 'rest', label: 'Rest' }, { id: 'confidence', label: 'Confidence' },
          { id: 'gratitude', label: 'Gratitude' }, { id: 'mindfulness', label: 'Mindfulness' },
          { id: 'routines', label: 'Routines' }, { id: 'boundaries', label: 'Boundaries' },
          { id: 'growth', label: 'Personal growth' }, { id: 'reflection', label: 'Reflection' },
        ],
      },
      {
        id: 'structure',
        title: 'Journal Structure',
        subtitle: 'How often will you journal?',
        fields: [
          { id: 'frequency', type: 'select', label: 'Journal frequency', required: true, options: [
            { id: 'daily', label: 'Daily' }, { id: 'weekly', label: 'Weekly' }, { id: 'both', label: 'Both' },
          ]},
        ]
      },
      {
        id: 'includes',
        title: 'What to Include?',
        subtitle: 'Select the features you want',
        type: 'multi-select',
        field: 'journalIncludes',
        required: true,
        options: [
          { id: 'mood-tracker', label: 'Mood tracker' }, { id: 'gratitude-prompts', label: 'Gratitude prompts' },
          { id: 'habit-tracker', label: 'Habit tracker' }, { id: 'affirmations', label: 'Affirmations' },
          { id: 'reflection-prompts', label: 'Reflection prompts' }, { id: 'self-care-ideas', label: 'Self-care ideas' },
        ],
      },
      {
        id: 'style',
        title: 'Design Aesthetic',
        subtitle: 'Choose a look',
        type: 'visual-select',
        field: 'designStyle',
        required: true,
        options: journalStyles,
      },
    ]
  },

  // ═══════════════════════════════════════
  // 16. SOFT LIFE PLANNER
  // ═══════════════════════════════════════
  'soft-life-planner': {
    productType: 'daily-planner',
    showCreatorToggle: true,
    creatorFields: [
      { id: 'targetBuyer', type: 'text', label: 'Target buyer', placeholder: 'e.g. Women embracing slow living' },
    ],
    steps: [
      {
        id: 'focus',
        title: 'What Does Your Ideal Soft Life Focus On?',
        subtitle: 'Select all that resonate',
        type: 'multi-select',
        field: 'softLifeFocus',
        required: true,
        options: [
          { id: 'self-care', label: 'Self-care' }, { id: 'slower-mornings', label: 'Slower mornings' },
          { id: 'wellness', label: 'Wellness' }, { id: 'productivity', label: 'Productivity without burnout' },
          { id: 'home', label: 'Home' }, { id: 'goals', label: 'Personal goals' },
          { id: 'finances', label: 'Finances' }, { id: 'relationships', label: 'Relationships' },
          { id: 'gratitude', label: 'Gratitude' },
        ],
      },
      {
        id: 'structure',
        title: 'Planner Structure',
        subtitle: 'How should it be organized?',
        type: 'multi-select',
        field: 'plannerSections',
        required: true,
        options: [
          { id: 'daily', label: 'Daily planner' }, { id: 'weekly', label: 'Weekly planner' },
          { id: 'morning-routine', label: 'Morning routine' }, { id: 'evening-routine', label: 'Evening routine' },
          { id: 'habit-tracking', label: 'Habit tracking' }, { id: 'goals', label: 'Goals' },
          { id: 'self-care-tracking', label: 'Self-care tracking' },
        ],
      },
      {
        id: 'style',
        title: 'Visual Aesthetic',
        subtitle: 'Choose your planner style',
        type: 'visual-select',
        field: 'designStyle',
        required: true,
        options: [
          { id: 'feminine-neutral', label: 'Feminine Neutral', desc: 'Soft beige and cream' },
          { id: 'blush', label: 'Blush', desc: 'Warm pink tones' },
          { id: 'warm-beige', label: 'Warm Beige', desc: 'Rich, cozy neutrals' },
          { id: 'sage', label: 'Sage', desc: 'Calming green tones' },
          { id: 'minimal-ivory', label: 'Minimal Ivory', desc: 'Clean and bright' },
          { id: 'soft-pastel', label: 'Soft Pastel', desc: 'Gentle mixed pastels' },
        ],
      },
    ]
  },

  // ═══════════════════════════════════════
  // 17. MEAL PLANNER
  // ═══════════════════════════════════════
  'meal-planner': {
    productType: 'meal-planner',
    showCreatorToggle: true,
    creatorFields: [
      { id: 'targetBuyer', type: 'text', label: 'Target buyer', placeholder: 'e.g. Busy families, meal prep enthusiasts' },
    ],
    steps: [
      {
        id: 'details',
        title: 'Your Meal Plan',
        subtitle: 'Set up your planner',
        fields: [
          { id: 'people', type: 'number', label: 'Number of people', required: true, placeholder: '2', min: 1, max: 12 },
          { id: 'days', type: 'select', label: 'Number of days', required: true, options: [
            { id: '5', label: '5 days' }, { id: '7', label: '7 days' },
          ]},
        ]
      },
      {
        id: 'meals',
        title: 'Meals & Priority',
        subtitle: 'What meals and what matters most?',
        fields: [
          { id: 'mealTypes', type: 'multi-select-inline', label: 'Meals to plan', required: true, options: [
            { id: 'breakfast', label: 'Breakfast' }, { id: 'lunch', label: 'Lunch' },
            { id: 'dinner', label: 'Dinner' }, { id: 'snacks', label: 'Snacks' },
          ]},
          { id: 'priority', type: 'select', label: 'Main priority', required: true, options: [
            { id: 'save-money', label: 'Save money' }, { id: 'eat-healthier', label: 'Eat healthier' },
            { id: 'save-time', label: 'Save time' }, { id: 'family-friendly', label: 'Family-friendly' },
            { id: 'meal-prep', label: 'Meal prep' }, { id: 'variety', label: 'Variety' },
          ]},
        ]
      },
      {
        id: 'auto',
        title: 'Auto-generate?',
        subtitle: 'Should Lumera create these for you?',
        type: 'multi-select',
        field: 'autoGenerate',
        required: false,
        options: [
          { id: 'weekly-menu', label: 'Weekly menu' }, { id: 'grocery-list', label: 'Grocery list' },
          { id: 'pantry-checklist', label: 'Pantry checklist' }, { id: 'mealprep-plan', label: 'Meal-prep plan' },
        ],
      },
    ]
  },

  // ═══════════════════════════════════════
  // 18. RECIPE BOOK
  // ═══════════════════════════════════════
  'recipe-book': {
    productType: 'recipe-book',
    showCreatorToggle: true,
    creatorFields: [
      { id: 'targetBuyer', type: 'text', label: 'Target buyer', placeholder: 'e.g. Home cooks, baking enthusiasts' },
    ],
    steps: [
      {
        id: 'details',
        title: 'Your Recipe Book',
        subtitle: 'Set up the basics',
        fields: [
          { id: 'bookTitle', type: 'text', label: 'Recipe book title', required: false, placeholder: 'e.g. Our Family Recipes' },
          { id: 'authorName', type: 'text', label: 'Author / family name', required: false, placeholder: 'Optional' },
          { id: 'bookType', type: 'select', label: 'Type of recipe book', required: true, options: [
            { id: 'family', label: 'Family recipes' }, { id: 'baking', label: 'Baking' },
            { id: 'healthy', label: 'Healthy recipes' }, { id: 'dinner', label: 'Dinner recipes' },
            { id: 'desserts', label: 'Desserts' }, { id: 'collection', label: 'Personal collection' },
            { id: 'blank', label: 'Blank recipe book' },
          ]},
        ]
      },
      {
        id: 'style',
        title: 'Book Style',
        subtitle: 'Choose the look',
        type: 'visual-select',
        field: 'bookStyle',
        required: true,
        options: [
          { id: 'warm-cozy', label: 'Warm & Cozy', desc: 'Rustic, inviting feel' },
          { id: 'minimal', label: 'Minimal', desc: 'Clean, modern layout' },
          { id: 'rustic', label: 'Rustic', desc: 'Farmhouse charm' },
          { id: 'modern', label: 'Modern', desc: 'Contemporary design' },
          { id: 'mediterranean', label: 'Mediterranean', desc: 'Sun-drenched warmth' },
          { id: 'botanical', label: 'Botanical', desc: 'Herb and garden inspired' },
          { id: 'vintage', label: 'Vintage', desc: 'Nostalgic, classic feel' },
        ],
      },
      {
        id: 'fields',
        title: 'Recipe Page Fields',
        subtitle: 'What should each recipe page include?',
        type: 'multi-select',
        field: 'recipeFields',
        required: true,
        options: [
          { id: 'ingredients', label: 'Ingredients' }, { id: 'instructions', label: 'Instructions' },
          { id: 'prep-time', label: 'Prep time' }, { id: 'cook-time', label: 'Cook time' },
          { id: 'servings', label: 'Servings' }, { id: 'notes', label: 'Notes' },
          { id: 'photo', label: 'Photo space' }, { id: 'rating', label: 'Rating' },
        ],
      },
    ]
  },

  // ═══════════════════════════════════════
  // 19. CONTENT PLANNER
  // ═══════════════════════════════════════
  'content-planner': {
    productType: 'business-planner',
    showCreatorToggle: true,
    creatorFields: [
      { id: 'targetBuyer', type: 'text', label: 'Target buyer', placeholder: 'e.g. Social media managers, coaches' },
    ],
    steps: [
      {
        id: 'business',
        title: 'Your Business / Content',
        subtitle: 'Tell us about your brand',
        fields: [
          { id: 'brandName', type: 'text', label: 'Brand / business name', required: false, placeholder: 'Optional' },
          { id: 'niche', type: 'text', label: 'Industry / niche', required: true, placeholder: 'e.g. Fitness coaching, Handmade jewelry' },
        ]
      },
      {
        id: 'platforms',
        title: 'Platforms & Goals',
        subtitle: 'Where do you post?',
        fields: [
          { id: 'platforms', type: 'multi-select-inline', label: 'Main platforms', required: true, options: [
            { id: 'instagram', label: 'Instagram' }, { id: 'tiktok', label: 'TikTok' },
            { id: 'pinterest', label: 'Pinterest' }, { id: 'youtube', label: 'YouTube' },
            { id: 'facebook', label: 'Facebook' }, { id: 'linkedin', label: 'LinkedIn' },
          ]},
          { id: 'contentGoal', type: 'select', label: 'Primary goal', required: true, options: [
            { id: 'grow-audience', label: 'Grow audience' }, { id: 'drive-sales', label: 'Drive sales' },
            { id: 'build-authority', label: 'Build authority' }, { id: 'generate-leads', label: 'Generate leads' },
            { id: 'increase-engagement', label: 'Increase engagement' },
          ]},
        ]
      },
      {
        id: 'includes',
        title: 'What to Include?',
        subtitle: 'Select planner features',
        type: 'multi-select',
        field: 'plannerIncludes',
        required: true,
        options: [
          { id: 'content-calendar', label: 'Content calendar' }, { id: 'idea-bank', label: 'Idea bank' },
          { id: 'caption-planner', label: 'Caption planner' }, { id: 'cta-planner', label: 'CTA planner' },
          { id: 'analytics-tracker', label: 'Analytics tracker' }, { id: 'campaign-planner', label: 'Campaign planner' },
        ],
      },
    ]
  },

  // ═══════════════════════════════════════
  // 20. ETSY SHOP PLANNER
  // ═══════════════════════════════════════
  'etsy-shop-planner': {
    productType: 'business-planner',
    showCreatorToggle: false,
    steps: [
      {
        id: 'shop',
        title: 'Your Etsy Shop',
        subtitle: 'Tell us about your business',
        fields: [
          { id: 'shopName', type: 'text', label: 'Shop name', required: false, placeholder: 'Optional' },
          { id: 'whatYouSell', type: 'text', label: 'What do you sell?', required: true, placeholder: 'e.g. Digital planners, printable art' },
          { id: 'shopStatus', type: 'select', label: 'Shop status', required: true, options: [
            { id: 'new', label: 'New shop' }, { id: 'existing', label: 'Existing shop' },
          ]},
          { id: 'primaryGoal', type: 'select', label: 'Primary goal', required: true, options: [
            { id: 'launch', label: 'Launch shop' }, { id: 'increase-sales', label: 'Increase sales' },
            { id: 'improve-listings', label: 'Improve listings' }, { id: 'grow-range', label: 'Grow product range' },
            { id: 'improve-seo', label: 'Improve SEO' },
          ]},
        ]
      },
      {
        id: 'needs',
        title: 'What Do You Need?',
        subtitle: 'Select what to include',
        type: 'multi-select',
        field: 'plannerSections',
        required: true,
        options: [
          { id: 'product-planning', label: 'Product planning' }, { id: 'keyword-research', label: 'Keyword research' },
          { id: 'listing-optimization', label: 'Listing optimization' }, { id: 'pricing', label: 'Pricing' },
          { id: 'sales-tracking', label: 'Sales tracking' }, { id: 'review-tracking', label: 'Review tracking' },
          { id: 'promotion', label: 'Promotion' }, { id: 'pinterest-marketing', label: 'Pinterest marketing' },
        ],
      },
    ]
  },

  // ═══════════════════════════════════════
  // 21. PINTEREST STRATEGY GUIDE
  // ═══════════════════════════════════════
  'pinterest-strategy-guide': {
    productType: 'guide',
    showCreatorToggle: false,
    steps: [
      {
        id: 'business',
        title: 'Your Pinterest Strategy',
        subtitle: 'Tell us about your business',
        fields: [
          { id: 'niche', type: 'text', label: 'Business / niche', required: true, placeholder: 'e.g. Home decor, Fashion' },
          { id: 'websiteType', type: 'text', label: 'Website / shop type', required: false, placeholder: 'e.g. Etsy shop, Blog, Shopify' },
          { id: 'pinterestGoal', type: 'select', label: 'Primary Pinterest goal', required: true, options: [
            { id: 'traffic', label: 'Website traffic' }, { id: 'etsy-sales', label: 'Etsy sales' },
            { id: 'product-sales', label: 'Product sales' }, { id: 'leads', label: 'Leads' },
            { id: 'brand-awareness', label: 'Brand awareness' },
          ]},
          { id: 'currentLevel', type: 'select', label: 'Current Pinterest level', required: true, options: [
            { id: 'new', label: 'Just starting' }, { id: 'some', label: 'Some experience' },
            { id: 'active', label: 'Active user' }, { id: 'advanced', label: 'Advanced' },
          ]},
        ]
      },
      {
        id: 'includes',
        title: 'What to Include?',
        subtitle: 'Select guide sections',
        type: 'multi-select',
        field: 'guideSections',
        required: true,
        options: [
          { id: 'posting-frequency', label: 'Posting frequency' }, { id: 'content-pillars', label: 'Content pillars' },
          { id: 'keywords', label: 'Keywords' }, { id: 'board-planning', label: 'Board planning' },
          { id: 'pin-concepts', label: 'Pin concepts' }, { id: 'analytics', label: 'Analytics' },
        ],
      },
    ]
  },

  // ═══════════════════════════════════════
  // 22. PUPPY JOURNAL
  // ═══════════════════════════════════════
  'puppy-journal': {
    productType: 'pet-journal',
    showCreatorToggle: true,
    creatorFields: [
      { id: 'targetBuyer', type: 'text', label: 'Target buyer', placeholder: 'e.g. New puppy owners' },
    ],
    steps: [
      {
        id: 'details',
        title: 'Your Puppy',
        subtitle: 'Tell us about your pup',
        fields: [
          { id: 'puppyName', type: 'text', label: 'Puppy name', required: true, placeholder: 'e.g. Luna' },
          { id: 'breed', type: 'text', label: 'Breed', required: false, placeholder: 'Optional' },
          { id: 'adoptionDate', type: 'date', label: 'Birthday / adoption date', required: false },
          { id: 'age', type: 'text', label: 'Age', required: false, placeholder: 'e.g. 3 months' },
        ]
      },
      {
        id: 'tracking',
        title: 'What to Track?',
        subtitle: 'Select all that apply',
        type: 'multi-select',
        field: 'trackingItems',
        required: true,
        options: [
          { id: 'growth', label: 'Growth' }, { id: 'training', label: 'Training' },
          { id: 'firsts', label: 'First experiences' }, { id: 'vaccinations', label: 'Vaccinations' },
          { id: 'vet-visits', label: 'Vet visits' }, { id: 'favorites', label: 'Favorite things' },
          { id: 'photos', label: 'Photos' }, { id: 'milestones', label: 'Milestones' },
        ],
      },
      {
        id: 'style',
        title: 'Design Style',
        subtitle: 'Choose a look',
        type: 'visual-select',
        field: 'designStyle',
        required: true,
        options: [
          { id: 'cute', label: 'Cute', desc: 'Fun and adorable' },
          { id: 'minimal', label: 'Minimal', desc: 'Clean and simple' },
          { id: 'neutral', label: 'Neutral', desc: 'Warm earth tones' },
          { id: 'colorful', label: 'Colorful', desc: 'Bright and playful' },
          { id: 'modern', label: 'Modern', desc: 'Sleek and contemporary' },
        ],
      },
    ]
  },

  // ═══════════════════════════════════════
  // 23. PET HEALTH RECORD
  // ═══════════════════════════════════════
  'pet-health-record': {
    productType: 'pet-journal',
    showCreatorToggle: false,
    steps: [
      {
        id: 'pet',
        title: 'Your Pet',
        subtitle: 'Set up the health record',
        fields: [
          { id: 'petName', type: 'text', label: 'Pet name', required: true, placeholder: 'e.g. Max' },
          { id: 'animalType', type: 'select', label: 'Animal type', required: true, options: [
            { id: 'dog', label: 'Dog' }, { id: 'cat', label: 'Cat' },
            { id: 'rabbit', label: 'Rabbit' }, { id: 'bird', label: 'Bird' }, { id: 'other', label: 'Other' },
          ]},
          { id: 'breed', type: 'text', label: 'Breed', required: false, placeholder: 'Optional' },
          { id: 'birthDate', type: 'date', label: 'Birth date', required: false },
        ]
      },
      {
        id: 'tracking',
        title: 'What to Track?',
        subtitle: 'Select health record sections',
        type: 'multi-select',
        field: 'healthSections',
        required: true,
        options: [
          { id: 'vaccinations', label: 'Vaccinations' }, { id: 'vet-appointments', label: 'Vet appointments' },
          { id: 'medication', label: 'Medication' }, { id: 'weight', label: 'Weight' },
          { id: 'allergies', label: 'Allrgyes' }, { id: 'procedures', label: 'Procedures' },
          { id: 'preventative', label: 'Preventative treatments' }, { id: 'notes', label: 'Notes' },
        ],
      },
    ]
  },

  // ═══════════════════════════════════════
  // 24. READING JOURNAL
  // ═══════════════════════════════════════
  'reading-journal': {
    productType: 'reading-journal',
    showCreatorToggle: true,
    creatorFields: [
      { id: 'targetBuyer', type: 'text', label: 'Target buyer', placeholder: 'e.g. Book lovers, book club members' },
    ],
    steps: [
      {
        id: 'goals',
        title: 'Your Reading Goals',
        subtitle: 'Set up your reading journal',
        fields: [
          { id: 'readingGoal', type: 'select', label: 'Reading goal', required: true, options: [
            { id: 'read-more', label: 'Read more books' }, { id: 'track-books', label: 'Track what I read' },
            { id: 'book-reviews', label: 'Write book reviews' }, { id: 'reading-challenge', label: 'Reading challenge' },
          ]},
          { id: 'genres', type: 'text', label: 'Preferred genres', required: false, placeholder: 'e.g. Fiction, Self-help, Mystery' },
          { id: 'yearlyGoal', type: 'text', label: 'Yearly / monthly goal', required: false, placeholder: 'e.g. 24 books this year' },
        ]
      },
      {
        id: 'features',
        title: 'Journal Features',
        subtitle: 'What pages do you want?',
        type: 'multi-select',
        field: 'journalFeatures',
        required: true,
        options: [
          { id: 'book-reviews', label: 'Book review pages' }, { id: 'reading-list', label: 'Reading list / TBR' },
          { id: 'quotes', label: 'Favorite quotes' }, { id: 'reading-log', label: 'Reading log' },
          { id: 'ratings', label: 'Ratings' }, { id: 'monthly-recap', label: 'Monthly recap' },
        ],
      },
      {
        id: 'style',
        title: 'Design Style',
        subtitle: 'Choose a look',
        type: 'visual-select',
        field: 'designStyle',
        required: true,
        options: journalStyles,
      },
    ]
  },

  // ═══════════════════════════════════════
  // 25. STUDY PLANNER
  // ═══════════════════════════════════════
  'study-planner': {
    productType: 'daily-planner',
    showCreatorToggle: true,
    creatorFields: [
      { id: 'targetBuyer', type: 'text', label: 'Target buyer', placeholder: 'e.g. College students, high schoolers' },
    ],
    steps: [
      {
        id: 'details',
        title: 'Your Studies',
        subtitle: 'Set up your study planner',
        fields: [
          { id: 'studyLevel', type: 'select', label: 'Study level', required: true, options: [
            { id: 'high-school', label: 'High school' }, { id: 'college', label: 'College / university' },
            { id: 'grad-school', label: 'Graduate school' }, { id: 'professional', label: 'Professional certification' },
          ]},
          { id: 'subjects', type: 'text', label: 'Subjects / courses', required: false, placeholder: 'e.g. Biology, Math, Literature' },
          { id: 'semesterDates', type: 'text', label: 'Semester dates', required: false, placeholder: 'e.g. Sep–Dec 2026' },
        ]
      },
      {
        id: 'features',
        title: 'What to Include?',
        subtitle: 'Select planner features',
        type: 'multi-select',
        field: 'plannerFeatures',
        required: true,
        options: [
          { id: 'assignment-tracker', label: 'Assignment tracker' }, { id: 'exam-schedule', label: 'Exam schedule' },
          { id: 'study-goals', label: 'Study goals' }, { id: 'time-blocks', label: 'Time blocks' },
          { id: 'note-pages', label: 'Note pages' }, { id: 'grade-tracker', label: 'Grade tracker' },
        ],
      },
    ]
  },

  // ═══════════════════════════════════════
  // 26. HOME MAINTENANCE LOG
  // ═══════════════════════════════════════
  'home-maintenance-log': {
    productType: 'checklist',
    showCreatorToggle: false,
    steps: [
      {
        id: 'home',
        title: 'Your Home',
        subtitle: 'Set up your maintenance log',
        fields: [
          { id: 'homeType', type: 'select', label: 'Home type', required: true, options: [
            { id: 'house', label: 'House' }, { id: 'apartment', label: 'Apartment' },
            { id: 'condo', label: 'Condo' }, { id: 'rental', label: 'Rental' },
          ]},
          { id: 'mainGoal', type: 'select', label: 'Main goal', required: true, options: [
            { id: 'stay-organized', label: 'Stay organized' }, { id: 'preventative', label: 'Preventative maintenance' },
            { id: 'track-repairs', label: 'Track repairs and costs' }, { id: 'seasonal', label: 'Seasonal maintenance' },
          ]},
        ]
      },
      {
        id: 'sections',
        title: 'What to Include?',
        subtitle: 'Select sections for your log',
        type: 'multi-select',
        field: 'logSections',
        required: true,
        options: [
          { id: 'seasonal-tasks', label: 'Seasonal task checklist' }, { id: 'repair-log', label: 'Repair log' },
          { id: 'vendor-contacts', label: 'Vendor / contractor contacts' }, { id: 'warranty-tracker', label: 'Warranty tracker' },
          { id: 'budget', label: 'Maintenance budget' }, { id: 'appliance-log', label: 'Appliance log' },
        ],
      },
    ]
  },

  // ═══════════════════════════════════════
  // 27. FAMILY WEEKLY ORGANIZER
  // ═══════════════════════════════════════
  'family-weekly-organizer': {
    productType: 'daily-planner',
    showCreatorToggle: true,
    creatorFields: [
      { id: 'targetBuyer', type: 'text', label: 'Target buyer', placeholder: 'e.g. Busy parents, families' },
    ],
    steps: [
      {
        id: 'family',
        title: 'Your Family',
        subtitle: 'Customize your organizer',
        fields: [
          { id: 'familySize', type: 'number', label: 'Family size', required: true, placeholder: '4', min: 1, max: 12 },
          { id: 'kidsAges', type: 'text', label: 'Kids\' ages', required: false, placeholder: 'e.g. 3, 7, 12' },
        ]
      },
      {
        id: 'sections',
        title: 'What to Include?',
        subtitle: 'Select organizer sections',
        type: 'multi-select',
        field: 'organizerSections',
        required: true,
        options: [
          { id: 'weekly-schedule', label: 'Weekly schedule' }, { id: 'meal-planning', label: 'Meal planning' },
          { id: 'chore-chart', label: 'Chore chart' }, { id: 'school-activities', label: 'School & activities' },
          { id: 'shopping-list', label: 'Shopping list' }, { id: 'family-goals', label: 'Family goals' },
          { id: 'notes', label: 'Notes' },
        ],
      },
    ]
  },

  // ═══════════════════════════════════════
  // 28. CHRISTMAS PLANNER
  // ═══════════════════════════════════════
  'christmas-planner': {
    productType: 'daily-planner',
    showCreatorToggle: true,
    creatorFields: [
      { id: 'targetBuyer', type: 'text', label: 'Target buyer', placeholder: 'e.g. Holiday planners, gift organizers' },
    ],
    steps: [
      {
        id: 'focus',
        title: 'Your Holiday Focus',
        subtitle: 'What matters most this Christmas?',
        type: 'multi-select',
        field: 'holidayFocus',
        required: true,
        options: [
          { id: 'gift-planning', label: 'Gift planning & budgeting' }, { id: 'decorating', label: 'Decorating' },
          { id: 'baking', label: 'Baking & cooking' }, { id: 'events', label: 'Events & parties' },
          { id: 'advent', label: 'Advent calendar' }, { id: 'traditions', label: 'Family traditions' },
          { id: 'cards', label: 'Holiday cards' }, { id: 'travel', label: 'Holiday travel' },
        ],
      },
      {
        id: 'style',
        title: 'Christmas Style',
        subtitle: 'Choose the aesthetic',
        type: 'visual-select',
        field: 'christmasStyle',
        required: true,
        options: [
          { id: 'classic-red', label: 'Classic Red & Green', desc: 'Traditional Christmas' },
          { id: 'elegant-gold', label: 'Elegant Gold', desc: 'Sophisticated, luxe feel' },
          { id: 'minimal-white', label: 'Minimal White', desc: 'Scandinavian simplicity' },
          { id: 'cozy-rustic', label: 'Cozy Rustic', desc: 'Warm, cabin-inspired' },
          { id: 'playful', label: 'Playful', desc: 'Fun, colorful, family-oriented' },
        ],
      },
    ]
  },

  // ═══════════════════════════════════════
  // 29. DATE NIGHT PLANNER
  // ═══════════════════════════════════════
  'date-night-planner': {
    productType: 'daily-planner',
    showCreatorToggle: true,
    creatorFields: [
      { id: 'targetBuyer', type: 'text', label: 'Target buyer', placeholder: 'e.g. Couples looking for date ideas' },
    ],
    steps: [
      {
        id: 'details',
        title: 'Your Date Nights',
        subtitle: 'Personalize your planner',
        fields: [
          { id: 'coupleNames', type: 'text', label: 'Your names', required: false, placeholder: 'Optional, e.g. Sarah & Tom' },
          { id: 'dateFrequency', type: 'select', label: 'How often do you date?', required: true, options: [
            { id: 'weekly', label: 'Weekly' }, { id: 'biweekly', label: 'Every two weeks' },
            { id: 'monthly', label: 'Monthly' }, { id: 'spontaneous', label: 'Spontaneous' },
          ]},
        ]
      },
      {
        id: 'interests',
        title: 'Date Night Interests',
        subtitle: 'What do you enjoy together?',
        type: 'multi-select',
        field: 'dateInterests',
        required: true,
        options: [
          { id: 'dining', label: 'Dining out' }, { id: 'cooking', label: 'Cooking at home' },
          { id: 'movies', label: 'Movies & shows' }, { id: 'outdoors', label: 'Outdoors & nature' },
          { id: 'games', label: 'Games & activities' }, { id: 'culture', label: 'Culture & art' },
          { id: 'adventure', label: 'Adventure' }, { id: 'relaxation', label: 'Relaxation & spa' },
        ],
      },
    ]
  },

  // ═══════════════════════════════════════
  // 30. ADHD FOCUS PLANNER
  // ═══════════════════════════════════════
  'adhd-focus-planner': {
    productType: 'daily-planner',
    showCreatorToggle: true,
    creatorFields: [
      { id: 'targetBuyer', type: 'text', label: 'Target buyer', placeholder: 'e.g. Adults with ADHD, neurodivergent professionals' },
    ],
    steps: [
      {
        id: 'challenges',
        title: 'Your Focus Challenges',
        subtitle: 'What do you struggle with most?',
        type: 'multi-select',
        field: 'challenges',
        required: true,
        options: [
          { id: 'focus', label: 'Staying focused' }, { id: 'starting', label: 'Starting tasks' },
          { id: 'time-management', label: 'Time management' }, { id: 'organization', label: 'Organization' },
          { id: 'overwhelm', label: 'Overwhelm' }, { id: 'routine', label: 'Building routines' },
          { id: 'deadlines', label: 'Meeting deadlines' }, { id: 'energy', label: 'Energy management' },
        ],
      },
      {
        id: 'features',
        title: 'Planner Features',
        subtitle: 'What tools would help you most?',
        type: 'multi-select',
        field: 'plannerFeatures',
        required: true,
        options: [
          { id: 'brain-dump', label: 'Brain dump pages' }, { id: 'time-blocks', label: 'Time blocking' },
          { id: 'task-breakdown', label: 'Task breakdown' }, { id: 'dopamine-menu', label: 'Dopamine menu' },
          { id: 'habit-tracker', label: 'Habit tracker' }, { id: 'priority-matrix', label: 'Priority matrix' },
          { id: 'energy-tracker', label: 'Energy tracker' }, { id: 'reward-system', label: 'Reward system' },
        ],
      },
    ]
  },

  // ═══════════════════════════════════════
  // 31. MOVING CHECKLIST
  // ═══════════════════════════════════════
  'moving-checklist': {
    productType: 'checklist',
    showCreatorToggle: false,
    steps: [
      {
        id: 'move',
        title: 'Your Move',
        subtitle: 'Set up your moving checklist',
        fields: [
          { id: 'moveDate', type: 'date', label: 'Moving date', required: false },
          { id: 'moveType', type: 'select', label: 'Type of move', required: true, options: [
            { id: 'local', label: 'Local move' }, { id: 'long-distance', label: 'Long distance' },
            { id: 'international', label: 'International' }, { id: 'first-home', label: 'First home' },
          ]},
          { id: 'household', type: 'select', label: 'Household size', required: true, options: [
            { id: 'solo', label: 'Solo / studio' }, { id: 'couple', label: 'Couple' },
            { id: 'family', label: 'Family' }, { id: 'large', label: 'Large household' },
          ]},
        ]
      },
      {
        id: 'sections',
        title: 'What to Include?',
        subtitle: 'Select checklist sections',
        type: 'multi-select',
        field: 'checklistSections',
        required: true,
        options: [
          { id: 'timeline', label: '8-week countdown' }, { id: 'packing', label: 'Room-by-room packing' },
          { id: 'utilities', label: 'Utilities & address changes' }, { id: 'cleaning', label: 'Cleaning checklist' },
          { id: 'budget', label: 'Moving budget' }, { id: 'important-docs', label: 'Important documents' },
        ],
      },
    ]
  },

  // ═══════════════════════════════════════
  // 32. GARDEN PLANNER
  // ═══════════════════════════════════════
  'garden-planner': {
    productType: 'daily-planner',
    showCreatorToggle: true,
    creatorFields: [
      { id: 'targetBuyer', type: 'text', label: 'Target buyer', placeholder: 'e.g. Home gardeners, plant lovers' },
    ],
    steps: [
      {
        id: 'garden',
        title: 'Your Garden',
        subtitle: 'Tell us about your space',
        fields: [
          { id: 'gardenType', type: 'select', label: 'Garden type', required: true, options: [
            { id: 'outdoor', label: 'Outdoor garden' }, { id: 'balcony', label: 'Balcony / patio' },
            { id: 'indoor', label: 'Indoor plants' }, { id: 'vegetable', label: 'Vegetable garden' },
            { id: 'herb', label: 'Herb garden' }, { id: 'mixed', label: 'Mixed' },
          ]},
          { id: 'experience', type: 'select', label: 'Experience level', required: true, options: [
            { id: 'beginner', label: 'Beginner' }, { id: 'intermediate', label: 'Some experience' },
            { id: 'experienced', label: 'Experienced' },
          ]},
        ]
      },
      {
        id: 'features',
        title: 'What to Include?',
        subtitle: 'Select planner features',
        type: 'multi-select',
        field: 'plannerFeatures',
        required: true,
        options: [
          { id: 'planting-calendar', label: 'Planting calendar' }, { id: 'plant-profiles', label: 'Plant profiles' },
          { id: 'watering-schedule', label: 'Watering schedule' }, { id: 'garden-layout', label: 'Garden layout' },
          { id: 'harvest-tracker', label: 'Harvest tracker' }, { id: 'seed-inventory', label: 'Seed inventory' },
          { id: 'journal', label: 'Garden journal' },
        ],
      },
    ]
  },
};

// ─── Digital Product Bundle has no personalization (it's a bundle, not a single product) ───
// 'digital-product-bundle' is intentionally excluded.

/**
 * Get the personalization schema for a product slug.
 * Returns null if no schema exists (product uses generic generator).
 */
export function getPersonalizationSchema(slug) {
  return schemas[slug] || null;
}

/**
 * Check if a product has a personalization schema.
 */
export function hasPersonalization(slug) {
  return slug in schemas;
}

/**
 * Get all product slugs that have personalization schemas.
 */
export function getPersonalizedProductSlugs() {
  return Object.keys(schemas);
}

export default schemas;
